import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlineAcademicCap, HiOutlineTrash, HiOutlineRefresh } from 'react-icons/hi';

export default function AdminSkills() {
  const { addToast } = useToast();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/skills').then(({ data }) => setSkills(data)).catch(() => setSkills([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (skill) => {
    const msg = skill.inUse
      ? `"${skill.name}" is currently used by a content generator. Deleting it will break that content type until it is restored on the next deploy. Delete anyway?`
      : `Delete the skill "${skill.name}"? This removes it from this environment.`;
    if (!window.confirm(msg)) return;
    setDeleting(skill.folder);
    try {
      await api.delete(`/admin/skills/${skill.folder}`);
      addToast('Skill deleted');
      setSkills(prev => prev.filter(s => s.folder !== skill.folder));
    } catch (err) {
      addToast(err.response?.data?.error || 'Delete failed', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><HiOutlineAcademicCap className="w-7 h-7 text-v2-blue" /> Skills</h1>
        <button onClick={load} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1"><HiOutlineRefresh className="w-4 h-4" /> Refresh</button>
      </div>
      <p className="text-gray-500 mb-6">Generation skills define how each content type is written. Deleting a skill removes it from this environment.</p>

      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {loading && <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>}
        {!loading && skills.length === 0 && <div className="p-8 text-center text-gray-400 text-sm">No skills found.</div>}
        {skills.map(s => (
          <div key={s.folder} className="p-4 flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 truncate">{s.name}</span>
                {s.inUse && <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">in use</span>}
              </div>
              {s.description && <div className="text-sm text-gray-500 mt-0.5">{s.description}</div>}
              <div className="text-xs text-gray-400 mt-0.5"><code>{s.folder}</code> · {s.sizeKb} KB</div>
            </div>
            <button onClick={() => handleDelete(s)} disabled={deleting === s.folder}
              title="Delete skill" className="p-2 text-red-400 hover:text-red-600 disabled:opacity-50 flex-shrink-0">
              <HiOutlineTrash className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
